import { frameDisplayUrl } from "../utils/assetMatching";
import { useMemo } from "react";
import type { FilmProject, Scene, StoryboardFrame, StoryboardFrameSourceAssignments } from "../types";
import { agentPlanSummary, goalLabel } from "../utils/analysisSummary";
import { frameBlockedByDisabledChunk, frameSourceClass, frameSourceLabel, frameStatus, formatRuntime, formatSceneChunking, sceneStatus } from "../utils/storyboard";
import { FrameEditor } from "./FrameEditor";
import { FrameSourceAssignments } from "./FrameSourceAssignments";
import { Icon } from "./Icon";

interface StoryboardWorkspaceProps {
  project: FilmProject;
  scenes: Scene[];
  activeScene: Scene;
  selectedFrame: StoryboardFrame;
  selectedFrameId: string;
  onSelectScene: (sceneId: string) => void;
  onSelectFrame: (frameId: string) => void;
  onToggleLock: () => void;
  onToggleQueue: () => void;
  onToggleDisable: () => void;
  onClearFrame: () => void;
  onUploadFrame: (file: File) => void;
  onOpenPrompt: () => void;
  onOpenAssetLibrary: () => void;
  onFrameSourceAssignmentsChange: (assignments?: StoryboardFrameSourceAssignments) => void;
}

export function StoryboardWorkspace({
  project,
  scenes,
  activeScene,
  selectedFrame,
  selectedFrameId,
  onSelectScene,
  onSelectFrame,
  onToggleLock,
  onToggleQueue,
  onToggleDisable,
  onClearFrame,
  onUploadFrame,
  onOpenPrompt,
  onOpenAssetLibrary,
  onFrameSourceAssignmentsChange
}: StoryboardWorkspaceProps) {
  const agentSummary = useMemo(() => agentPlanSummary(project.apiPlan || {}, project), [project]);

  return (
    <section className="storyboard-stage" aria-label="Storyboard workspace">
      <header className="stage-heading">
        <div className="stage-heading-copy">
          <span>{`Scene ${String(activeScene.number).padStart(2, "0")} · ${activeScene.frames.length} boards · ${formatSceneChunking(activeScene)}`}</span>
          <h1>{activeScene.slugline}</h1>
          <p>{activeScene.synopsis}</p>
        </div>
        <div className="stage-heading-panel">
          <ProductionAgentChip project={project} activeScene={activeScene} summary={agentSummary} />
          <div className="stage-tools">
            <button className="secondary-action compact-action" type="button" onClick={onOpenAssetLibrary}>
              <Icon name="image" />
              Asset Library
            </button>
            <button className="icon-button" type="button" aria-label="Storyboard grid"><Icon name="boards" /></button>
            <button className="icon-button" type="button" aria-label="Shot list"><Icon name="script" /></button>
          </div>
        </div>
      </header>

      <div className="board-grid">
        {activeScene.frames.map((frame, index) => {
          const chunkDisabled = frameBlockedByDisabledChunk(activeScene, frame);
          return (
            <button
              className={[
                "board-card",
                index === 0 ? "hero" : "",
                frame.id === selectedFrameId ? "selected" : "",
                frame.locked ? "locked" : "",
                frame.queuedRegenerate || frame.generationStatus === "queued" ? "queued" : "",
                frame.generationStatus === "generating" ? "generating" : "",
                frame.generationStatus === "failed" ? "failed" : "",
                frame.generationStatus === "generated" || frame.generationStatus === "saved" ? "generated" : "",
                frame.disabled || chunkDisabled ? "disabled" : "",
                chunkDisabled ? "chunk-disabled" : "",
                frame.source === "uploaded" ? "uploaded" : ""
              ].filter(Boolean).join(" ")}
              key={frame.id}
              onClick={() => onSelectFrame(frame.id)}
              type="button"
            >
              <img src={frameDisplayUrl(frame)} alt={frame.label} />
              <span className="board-status">{chunkDisabled ? "Chunk Disabled" : frameStatus(frame)}</span>
              <span className={`board-source ${frameSourceClass(frame)}`}>{frameSourceLabel(frame)}</span>
              <span className="board-caption">
                <em>{index + 1}</em>
                <strong>{frame.shotType}</strong>
                <small>{frame.durationSeconds.toFixed(1)}s</small>
              </span>
              {frame.blockingPlan ? (
                <span className="board-blocking">{frame.blockingPlan.storyChange || frame.blockingPlan.movementPath}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <FrameEditor
        frame={selectedFrame}
        onToggleLock={onToggleLock}
        onToggleQueue={onToggleQueue}
        onToggleDisable={onToggleDisable}
        onClear={onClearFrame}
        onUpload={onUploadFrame}
        onOpenPrompt={onOpenPrompt}
      />

      <FrameSourceAssignments
        project={project}
        scene={activeScene}
        frame={selectedFrame}
        onChange={onFrameSourceAssignmentsChange}
      />

      <BlockingPlanPanel frame={selectedFrame} />

      <footer className="scene-strip" aria-label="Scene strip">
        <div>
          <strong>Scenes</strong>
          <span>{activeScene.frames.filter((frame) => !frame.disabled).length} included boards</span>
        </div>
        <div className="scene-strip-scroll">
          {scenes.map((scene) => (
            <button className={scene.id === activeScene.id ? "selected" : ""} key={scene.id} onClick={() => onSelectScene(scene.id)} type="button">
              <img src={scene.frames[0] ? frameDisplayUrl(scene.frames[0]) : ""} alt="" />
              <strong>{String(scene.number).padStart(2, "0")}</strong>
              <span>{scene.slugline}</span>
              <em className={`readiness ${scene.readiness}`}>{sceneStatus(scene)}</em>
              <small>{formatRuntime(scene.durationSeconds)} · {formatSceneChunking(scene)}</small>
            </button>
          ))}
          <button className="add-scene" type="button">
            <Icon name="plus" />
            Add Scene
          </button>
        </div>
      </footer>
    </section>
  );
}

function BlockingPlanPanel({ frame }: { frame: StoryboardFrame }) {
  const blocking = frame.blockingPlan;
  if (!blocking) return null;
  const marks = (blocking.characterMarks || [])
    .map((mark) => [mark.character, mark.framePosition, mark.action].filter(Boolean).join(": "))
    .filter(Boolean);
  return (
    <section className="blocking-plan-panel" aria-label="Shot blocking plan">
      <header>
        <span>Blocking</span>
        <strong>{blocking.objectMark || "No object mark"}</strong>
      </header>
      <div className="blocking-plan-grid">
        <div>
          <span>Marks</span>
          <strong>{marks.length ? marks.join(" · ") : "No character marks"}</strong>
        </div>
        <div>
          <span>Movement</span>
          <strong>{blocking.movementPath || "Hold frame"}</strong>
        </div>
        <div>
          <span>Axis</span>
          <strong>{blocking.cameraAxis || "Axis unset"}</strong>
        </div>
        <div>
          <span>End Frame</span>
          <strong>{blocking.endFrame || blocking.storyChange || "End state unset"}</strong>
        </div>
      </div>
      {blocking.continuityRisk ? <p>{blocking.continuityRisk}</p> : null}
    </section>
  );
}

function ProductionAgentChip({
  project,
  activeScene,
  summary
}: {
  project: FilmProject;
  activeScene: Scene;
  summary: ReturnType<typeof agentPlanSummary>;
}) {
  const activeSceneBeat = summary.beats.find((beat) => beat.sceneLabel.includes(`Sc ${activeScene.number}`));
  const needsInputCount = summary.beats.filter((beat) => beat.readiness === "needs_input").length;
  const missingSourceCount = project.characters.filter((character) => character.cardStatus === "missing" || character.voiceStatus === "missing").length
    + project.scenes.filter((scene) => scene.readiness !== "ready").length;
  return (
    <aside className="agent-chip" aria-label="Kinetix Production Agent plan status">
      <div className="agent-chip-heading">
        <span className="agent-dot" />
        <strong>Kinetix Production Agent</strong>
      </div>
      <div className="agent-chip-meta" aria-label="Production agent summary">
        <span>{goalLabel(summary.goal)} plan</span>
        <span>{summary.confidence}% confidence</span>
        <span>{summary.beats.length} beats</span>
      </div>
      <p>
        {activeSceneBeat
          ? `${activeSceneBeat.label}: ${activeSceneBeat.note}`
          : `${formatRuntime(summary.runtimeSeconds)} target · ${summary.strategy}`}
      </p>
      <div className="agent-chip-status">
        <em>Dry run only</em>
        <em className={needsInputCount || missingSourceCount ? "needs-input" : "ready"}>
          {needsInputCount || missingSourceCount
            ? `${needsInputCount + missingSourceCount} source check${needsInputCount + missingSourceCount === 1 ? "" : "s"}`
            : "Ready for boards"}
        </em>
      </div>
    </aside>
  );
}
